/* eslint-disable no-cond-assign */
/* eslint-disable no-constant-condition */
import * as signalR from '@microsoft/signalr'
import { setIsPaymentEnable } from 'features/restaurant/restaurant'
import {
  setPosPay,
  setTabDetail,
  setTabItems,
  setTabSeats,
  setUpdatedItems,
  updateOrderStatusFromSignalR,
  updateMultipleOrderStatusesFromSignalR,
} from 'features/tab/tab'
import { ISplitData, ITabDetail, ITabItem, ITableService } from 'interfaces/tab'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { store } from 'store'
import { itemsToSeat } from 'utility/helper'
import { resolvePrintedPosTableId } from 'utility/printedTableAlias'

const useSignalR = () => {
  const dispatch = useDispatch()
  const searchParameters = new URLSearchParams(
    window.location.search.split('?')[1]
  )
  // Same rule as App: the id off the sticker is corrected, the stored one already is.
  const establishmentId =
    searchParameters.get('eId') || sessionStorage.getItem('establishmentId')
  const tableId = searchParameters.get('pId')
    ? resolvePrintedPosTableId(establishmentId, searchParameters.get('pId'))
    : sessionStorage.getItem('posTableId')

  useEffect(() => {
    const hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_API_SERVER}/notify-client-connect`)
      .configureLogging(signalR.LogLevel.Information)
      .withAutomaticReconnect()
      .build()
    // Expose the connection so usePaymentProcessing can attach payment-confirmation
    // listeners (it reads window.signalRConnection as its fast path before polling).
    ;(window as any).signalRConnection = hubConnection

    // Once the hook is cleaned up, no retry may start the stopped connection again.
    let disposed = false
    let retryTimer: ReturnType<typeof setTimeout> | undefined

    const stopConnection = () => {
      disposed = true
      if (retryTimer) clearTimeout(retryTimer)
      if ((window as any).signalRConnection === hubConnection) {
        ;(window as any).signalRConnection = undefined
      }
      hubConnection.stop()
    }
    if (!tableId) {
      return stopConnection
    }

    // Starts the SignalR connection with retry
    const startWithRetry = (retryCount = 0, maxRetries = 3) => {
      if (disposed) return
      hubConnection
        .start()
        .then(async () => {
          console.log('🔗 SignalR connected - Table:', tableId)

          // Auto-register guest connection for broadcasting
          hubConnection
            .invoke('RegisterGuestConnection', parseInt(tableId, 10), null)
            .then(() => {
              console.log('✅ Registered for table broadcasts')
            })
            .catch((error) => {
              console.error('❌ Registration failed:', error)
            })
        })
        .catch((error) => {
          if (disposed) return
          console.error(
            `⚠️ SignalR failed (${retryCount + 1}/${maxRetries}), retrying...`,
            error
          )

          if (retryCount < maxRetries) {
            // Shorter exponential backoff with a maximum delay of 5 seconds
            const delay = Math.min(1 * 1.5 ** retryCount, 5) * 1000
            retryTimer = setTimeout(
              () => startWithRetry(retryCount + 1, maxRetries),
              delay
            )
          } else {
            console.error('❌ SignalR connection failed permanently')
            // Throw an error to indicate connection failure
            throw new Error(
              'SignalR connection failed after maximum retry attempts'
            )
          }
        })
    }
    startWithRetry()

    // Handlers are kept as named references so cleanup can detach exactly these
    // (an unqualified off(event) would also strip the payment-confirmation
    // listeners usePaymentProcessing attaches to the same connection).
    const handleTabData = async (
      response: ITabDetail | ISplitData | string | ITableService
    ) => {
        console.log('signal', response)
        if ((response as string) === 'table closed') {
          dispatch(
            setIsPaymentEnable({
              status: true, // Table is healthy
              isTableDisable: false, // Table is not disabled
              isHealthy: true, // System is healthy
              tableId: parseInt(tableId, 10), // Current table ID
            })
          )
          const userDestination = window.location.pathname
          sessionStorage.setItem('tabId', 'closed')
          if (userDestination === '/your-tab' || userDestination === '/') {
            dispatch(setTabSeats([]))
            dispatch(setTabItems([]))
            dispatch(setTabDetail(null))
          }
          return
        }

        if (
          (response as ITableService).isTableOutOfService ||
          (response as ITableService).isTableOutOfService === false
        ) {
          dispatch(
            setIsPaymentEnable({
              status: true, // Table is healthy
              isTableDisable: (response as ITableService).isTableOutOfService,
              isHealthy: true, // System is healthy
              tableId: parseInt(tableId, 10), // Current table ID
            })
          )
          return
        }
        if (
          (response as ITabDetail).orders &&
          (response as ITabDetail).orders.length > 0
        ) {
          const tabDetailed = response as ITabDetail
          const userDestination = window.location.pathname
          if (
            tabDetailed?.tabPayments?.isPosPaid === true &&
            userDestination === '/payment'
          ) {
            dispatch(setPosPay(true))
            tabDetailed.orders.forEach((element) => {
              element.orderDate = null
            })
            // const rawData = JSON.stringify(tabDetailed)
            // dispatch(setRawTabDetail(rawData))
            sessionStorage.setItem('tabId', tabDetailed.tabId.toString())
            tabDetailed.orders.forEach((element) => {
              element.orderDate = null
              element.isSelected = true
              element.isPaid = false
            })
            // dispatch(setTabDetail(tabDetailed))
            if (tabDetailed.orders?.length > 0) {
              let allItems: ITabItem[] = []
              tabDetailed.orders.forEach((element) => {
                allItems = [...allItems, ...element.items]
              })
              if (allItems?.length > 0) {
                dispatch(setTabSeats(itemsToSeat(allItems)))
                dispatch(setTabItems(allItems))
              }
            }
          } else if (
            userDestination === '/your-tab' ||
            userDestination === '/'
          ) {
            tabDetailed.orders.forEach((element) => {
              element.orderDate = null
            })
            sessionStorage.setItem('tabId', tabDetailed.tabId.toString())
            // eslint-disable-next-line no-cond-assign
            const data = store.getState().tab?.tabDetailData
            tabDetailed?.orders?.forEach((element) => {
              element.orderDate = null
              if (data === null) {
                element.isSelected = true
              } else {
                const cOrder = data?.orders?.find(
                  (d) => d.orderId === element.orderId
                )
                if (cOrder) {
                  element.isSelected = cOrder.isSelected
                } else {
                  element.isSelected = true
                }
              }
              element.isPaid = false
            })
            dispatch(setTabDetail(tabDetailed))
            if (tabDetailed.paymentModeId) {
              if (
                tabDetailed?.orders?.length > 1 &&
                tabDetailed?.paymentModeId === 6
              ) {
                sessionStorage.setItem('paymentModeId', null)
              } else {
                sessionStorage.setItem(
                  'paymentModeId',
                  tabDetailed.paymentModeId.toString()
                )
              }
            }
            if (tabDetailed.orders?.length > 0) {
              let allItems: ITabItem[] = []
              if (tabDetailed.tabStatus.toLowerCase() === 'changed') {
                tabDetailed.orders.forEach((element) => {
                  allItems = [...allItems, ...element.items]
                })
                dispatch(setUpdatedItems(allItems))
              } else {
                tabDetailed.orders.forEach((element) => {
                  allItems = [...allItems, ...element.items]
                })
                if (allItems?.length > 0) {
                  dispatch(setTabSeats(itemsToSeat(allItems)))
                  dispatch(setTabItems(allItems))
                }
              }
            }

            // Refresh payment status when tab data is updated via SignalR
            if (userDestination === '/your-tab' || userDestination === '/') {
              try {
                const tabService = (await import('../services/tab.service'))
                  .default
                const paymentStatusRes = await tabService.getIsPaymentEnable({
                  tableId: Number(sessionStorage.getItem('tid')),
                })
                dispatch(setIsPaymentEnable(paymentStatusRes.data.data))
              } catch (error) {
                console.warn(
                  'Failed to refresh payment status via SignalR:',
                  error
                )
              }
            }
          }
          // end
        }
    }

    // The hub serializes payloads with PascalCase property names (FOH contract),
    // so read both casings and normalize the status to lowercase before dispatch.
    const normalizeOrderStatusUpdate = (update: any) => ({
      orderId: update?.OrderId ?? update?.orderId,
      status: (update?.Status ?? update?.status ?? '').toLowerCase(),
      updatedAt: update?.UpdatedAt ?? update?.updatedAt,
    })

    const handleOrderStatusUpdate = (orderStatusUpdate: any) => {
      const normalized = normalizeOrderStatusUpdate(orderStatusUpdate)
      console.log(
        '📨 Order status update:',
        normalized.orderId,
        '→',
        normalized.status
      )

      dispatch(updateOrderStatusFromSignalR(normalized))
    }

    const handleMultipleOrderStatusUpdate = (orderStatusUpdates: any[]) => {
      if (!Array.isArray(orderStatusUpdates)) {
        console.warn('Invalid multiple order-status update payload')
        return
      }

      console.log(
        '📨 Multiple order updates:',
        orderStatusUpdates?.length || 0,
        'orders'
      )

      dispatch(
        updateMultipleOrderStatusesFromSignalR(
          orderStatusUpdates.map(normalizeOrderStatusUpdate)
        )
      )
    }

    hubConnection.on('broadcastyourtabdata', handleTabData)
    hubConnection.on('OnOrderStatusUpdate', handleOrderStatusUpdate)
    hubConnection.on('OnMultipleOrderStatusUpdate', handleMultipleOrderStatusUpdate)

    return () => {
      // Detach only this hook's handlers - payment-confirmation listeners
      // registered by usePaymentProcessing must survive until their own cleanup.
      hubConnection.off('broadcastyourtabdata', handleTabData)
      hubConnection.off('OnOrderStatusUpdate', handleOrderStatusUpdate)
      hubConnection.off('OnMultipleOrderStatusUpdate', handleMultipleOrderStatusUpdate)
      stopConnection()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

export default useSignalR
